import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import d3 from './lib/d3';

declare const salaryChartLabels:{
  location: string;
  jobTitle: string;
  yourSalary: string;
  lowSalary: string;
  medianSalary: string;
  highSalary: string;
};


const tag = 'check-salary-form-data-visualizer';

@customElement(tag)
export class DataVisualizer extends LitElement {

  // Allows for importing the module and telling the build tools a side effect occurred. Goal: Reduce filesize if not in use.
  enabled = true;

  // Exposed properties
  @property({ type: Object }) data;

  @property() type = 'compare';

  // @property() chartId = 'salary-data-viz';

  @property() country = '';

  @property() market = '';

  @property({ type: String, attribute: 'job-title-name' }) jobTitleName = '';

  @property({ type: Number }) salary = null;

  // Forces the removal of the Shadow DOM for global style usage
  protected createRenderRoot(): Element | ShadowRoot {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('resize', () => this.generateChart(), false);
    window.addEventListener('orientationchange', () => this.generateChart(), false);
  }

  protected firstUpdated(): void {
    this.generateChart();
  }

  getCurrencySymbol(): string {
    const currencySymbols: { [key: string]: string } = {
      US: '$',
      CA: '$',
      UK: '£',
      NL: '€',
      DE: '€',
      FR: '€',
      AU: '$',
      JP: '¥',
    };
    return currencySymbols[this.country] || '$'; // Default to USD if country not found
  }

  generateChart() {

    const margin = {
      top: 43,
      right: 60,
      bottom: 30,
      left: 94,
    };

    const chartId = this.type === 'compare' ? 'data-viz-compare' : 'data-viz-search';
    const container = d3.select(this.renderRoot.querySelector(`#${chartId}`));
    console.log('container generate chart ', container);
    if (!container) {
      return
    }

    const containerNode = d3.select(`#${chartId}`).node();

    // Determine the width and height based off the div on the page
    const width = containerNode.clientWidth - margin.left - margin.right;
    const height = containerNode.clientHeight - margin.top - margin.bottom;
    console.log(`width: ${width}, height: ${height}  -
       containerNode.clientWidth: ${containerNode.clientWidth} - containerNode.clientHeight: ${containerNode.clientHeight}
       margin.left: ${margin.left} - margin.right: ${margin.right} - margin.top: ${margin.top} - margin.bottom: ${margin.bottom}`);
 // const contaicontainer = d3.seleas Selection;
 const containerSvg = container.selectAll('svg');
 if (containerSvg) {
   containerSvg.remove();
 }
    // Some base data for the graph
    const {
      low, median, high, country,
    } = this.data;

    // Init the data
    const data = [{
      name: salaryChartLabels.highSalary,
      value: high,
      class: 'high',
    }, {
      name: salaryChartLabels.medianSalary,
      value: median,
      class: 'median',
    }, {
      name: salaryChartLabels.lowSalary,
      value: low,
      class: 'low',
    }];

    // If we are searching, we don't need to show `your salary`
    if (this.salary > 0) {
      data.push({
        name: salaryChartLabels.yourSalary,
        value: this.salary,
        class: 'yours',
      });
    }

    // Sort Data
    data.sort((a, b) => a.value - b.value);
    const currencySymbol = this.getCurrencySymbol();
    const locale = d3.formatLocale({
      decimal: '.',
      thousands: ',',
      grouping: [3],
      currency: [currencySymbol, ''],
    });
    const dollarFormatter = locale.format('$,.0f');

    // cache the graph width as variables
    const graphWidth = width + margin.left + margin.right;
    const graphHeight = height + margin.top + margin.bottom;

    const svg = container.append('svg')
    .attr('id', 'bar-chart')
      .attr('class', 'bar-chart')
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('viewBox', `-${margin.left} -${margin.top} ${graphWidth} ${graphHeight}`)
      .attr('width', graphWidth)
      .attr('height', graphHeight)
      .append('g')
      .attr('id', 'bar-chart-group')
      .attr('class', 'bar-chart-group');

    // Get the maximum value from our data
    const min = d3.min(data, (d) => parseInt(d.value));
    const max = d3.max(data, (d) => parseInt(d.value));
    const range = max - min;

    const startpoint = Math.floor(min / range) * range;
    const endpoint = Math.ceil(max / range) * range;

    // Creates the X Axis Scale
    const x = d3.scaleLinear()
      .rangeRound([0, width])
      .domain([startpoint, endpoint]);

    // Creates the Y Axis Scale
    const y = d3.scaleBand()
      .range([height, 0])
      .domain(data.map((d) => d.name))
      // change the height of the spacing between the bars
      .paddingOuter(0.3) // padding on the between the bars and edges of graph
      .paddingInner(0.4); // padding between bars

    // Defines the y axis styles
    const yAxis = d3.axisLeft(y).scale(y);

    // Defines the X axis styles
    const xAxis = d3.axisBottom(x).scale(x);

    // set suggested number of ticks, format the output
    xAxis.ticks(5, '$,.0f').tickFormat(locale.format('$,.0f'));

    svg.append('g')
      .attr('class', 'y axis')
      .call(yAxis);

    // X Axis Labels
    svg.append('g')
      .attr('class', 'x axis')
      .call(xAxis)
      .attr('transform', `translate(0,${height})`)
      .selectAll('text')
      // Rotate the labels 45°
      .attr('transform', 'translate(-10,0) rotate(-45)')
      .style('text-anchor', 'end');

    const bars = svg.selectAll('.bar')
      .data(data)
      .enter();

    // Add bars (rectangles) and animate them
    bars.append('rect')
      .attr('class', (d) => `bar ${d.class}`)
      .attr('y', (d) => y(d.name))
      .attr('height', y.bandwidth())
      // animate width (set the initial width to zero)
      .attr('width', 0)
      .transition()
      // time in ms
      .duration(1000)
      // stagger each bar by 50ms for a little variety
      .delay((d, i) => i * 50)
      // this is the width after the animation
      .attr('width', (d) => x(d.value));

    // Add bar labels
    bars.append('text')
      .attr('class', 'bar-label')
      .attr('y', (d) => y(d.name) + y.bandwidth() / 2 + 4)
      .attr('x', (d) => x(d.value) + 3)
      .text((d) => dollarFormatter(d.value));
  }

  render() {
    // if(Number.isNaN(this.salary)) {
    //   this.chartId = `search-salary-data-viz`;
    // }else{
    //   this.chartId = `compare-salary-data-viz`;
    // }
    console.log('Render this.type: ', this.type);
    const chartId = this.type === 'compare' ? 'data-viz-compare' : 'data-viz-search';
    console.log('chartId render ', chartId);
    return html`
      <div id="data-viz-wrapper" class=${classMap({ 'data-viz-wrapper': true, shorter: !this.salary })}>
        <figure id="${chartId}" class="salary-data-viz shorter">
          <figcaption class="show-for-sr">
            <table>
              <thead>
                <tr>
                  <th>${salaryChartLabels.location}</th>
                    <!-- ## TODO -- we will need an API call to support this ... -->
                   <!-- <th class="job-title-th">37 Salary Results for CX Lead</th> -->
                   <th class="job-title-th">${salaryChartLabels.jobTitle}</th>
                   ${this.type === 'compare' ? html`<th>${salaryChartLabels.yourSalary}</th>` : html``}
                  <th>${salaryChartLabels.lowSalary}</th>
                  <th>${salaryChartLabels.medianSalary}</th>
                  <th>${salaryChartLabels.highSalary}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="user-location">${this.market ? this.market : this.country}</td>
                  <td class="job-title">${this.jobTitleName}</td>
                  ${this.type === 'compare' ? html`<td class="user-salary">${this.salary}</td>` : html``}
                  <td class="low-salary">${this.data.low}</td>
                  <td class="median-salary">${this.data.median}</td>
                  <td class="high-salary">${this.data.high}</td>
                </tr>
              </tbody>
            </table>
          </figcaption>
        </figure>
      </div>
    `;
  }
}
